// components/RegionalStackedCards.jsx
import { useMemo, useState } from 'react';

/**
 * Normalize district name for matching
 */
function normalizeDistrictName(name) {
  return name
    .toLowerCase()
    .replace(/\s*(school district|sd|district|schools?)\s*/gi, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();
}

/**
 * Generate a color palette for CIPs
 * Uses soft, muted colors that work well together
 */
const CIP_COLORS = [
  '#6366f1', // indigo
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#f43f5e', // rose
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#14b8a6', // teal
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#a855f7', // purple
  '#d946ef', // fuchsia
];

/**
 * Regional Stacked Cards
 * Three cards side by side (one per region)
 * Each district shows a stacked horizontal bar with CIP breakdown
 */
export default function RegionalStackedCards({
  cipEnrolled,
  filterSession,
  nontrad,
  yearIndex = 4,
  regionalGroups = {},
  regionalColors = {}
}) {
  const [hoveredCip, setHoveredCip] = useState(null);
  const [showLegend, setShowLegend] = useState(true);
  const [viewMode, setViewMode] = useState('raw'); // 'raw' | 'percent'

  // Build normalized lookup map for regions
  const regionLookup = useMemo(() => {
    const lookup = new Map();
    Object.entries(regionalGroups).forEach(([groupName, schools]) => {
      schools.forEach(school => {
        lookup.set(normalizeDistrictName(school), groupName);
      });
    });
    return lookup;
  }, [regionalGroups]);

  // Helper to check if a CIP matches the current filter
  const matchesFilter = (item) => {
    if (filterSession === "nontrad-female") {
      return nontrad?.female?.includes(item.cipcode);
    } else if (filterSession === "nontrad-male") {
      return nontrad?.male?.includes(item.cipcode);
    }
    return item.clusterName === filterSession || item.programArea === filterSession;
  };

  // Get district's regional group
  const getRegion = (schoolName) => {
    const normalized = normalizeDistrictName(schoolName);
    if (regionLookup.has(normalized)) {
      return regionLookup.get(normalized);
    }
    for (const [normalizedSchool, groupName] of regionLookup.entries()) {
      if (normalized.startsWith(normalizedSchool) || normalizedSchool.startsWith(normalized)) {
        return groupName;
      }
    }
    return "Other";
  };

  // Process data into region cards format
  const cardData = useMemo(() => {
    if (!cipEnrolled?.length) return { regions: [], cipLegend: [], maxEnrollment: 1 };

    const filteredCips = cipEnrolled.filter(matchesFilter);

    // Build CIP info and color mapping
    const cipMap = new Map();
    filteredCips.forEach((cip, idx) => {
      cipMap.set(cip.cipcode, {
        code: cip.cipcode,
        name: cip.cipcodeName,
        color: CIP_COLORS[idx % CIP_COLORS.length]
      });
    });

    // Build district data grouped by region
    // Initialize ALL districts from regionalGroups (including those with zero enrollment)
    const regionData = {};
    Object.entries(regionalGroups).forEach(([region, districts]) => {
      regionData[region] = {
        name: region,
        color: regionalColors[region] || '#6b7280',
        districts: {}
      };
      // Pre-populate all districts with zero enrollment
      districts.forEach(districtName => {
        regionData[region].districts[districtName] = {
          name: districtName,
          total: 0,
          cips: []
        };
      });
    });
    regionData["Other"] = {
      name: "Other",
      color: '#6b7280',
      districts: {}
    };

    // Process enrollments
    filteredCips.forEach(cip => {
      if (!cip.enrolled) return;

      cip.enrolled.forEach(school => {
        if (school.schoolName === "Total") return;

        const enrollment = school.enrollment?.[yearIndex] ?? 0;
        if (enrollment === 0) return;

        const region = getRegion(school.schoolName);
        if (!regionData[region]) return;

        // Create district entry if it doesn't exist (for "Other" region)
        if (!regionData[region].districts[school.schoolName]) {
          regionData[region].districts[school.schoolName] = {
            name: school.schoolName,
            total: 0,
            cips: []
          };
        }

        regionData[region].districts[school.schoolName].cips.push({
          code: cip.cipcode,
          name: cip.cipcodeName,
          enrollment,
          color: cipMap.get(cip.cipcode)?.color || '#9ca3af'
        });
        regionData[region].districts[school.schoolName].total += enrollment;
      });
    });

    // Convert to array and sort districts by total enrollment
    const regions = Object.values(regionData)
      .filter(r => Object.keys(r.districts).length > 0)
      .map(r => ({
        ...r,
        districts: Object.values(r.districts)
          .sort((a, b) => b.total - a.total)
          .map(d => ({
            ...d,
            cips: d.cips.sort((a, b) => b.enrollment - a.enrollment)
          })),
        total: Object.values(r.districts).reduce((sum, d) => sum + d.total, 0)
      }))
      .sort((a, b) => {
        // Keep regions in order, put "Other" last
        if (a.name === "Other") return 1;
        if (b.name === "Other") return -1;
        const order = Object.keys(regionalGroups);
        return order.indexOf(a.name) - order.indexOf(b.name);
      });

    // Find max district enrollment for consistent scaling
    let maxEnrollment = 1;
    regions.forEach(r => {
      r.districts.forEach(d => {
        if (d.total > maxEnrollment) maxEnrollment = d.total;
      });
    });

    // Build CIP legend (only CIPs that have enrollment)
    const usedCips = new Set();
    regions.forEach(r => {
      r.districts.forEach(d => {
        d.cips.forEach(c => usedCips.add(c.code));
      });
    });
    const cipLegend = Array.from(cipMap.values())
      .filter(c => usedCips.has(c.code))
      .sort((a, b) => a.name.localeCompare(b.name));

    return { regions, cipLegend, maxEnrollment };
  }, [cipEnrolled, filterSession, nontrad, yearIndex, regionalGroups, regionalColors]);

  // Calculate max for percentage mode (region's max total)
  const regionMaxes = useMemo(() => {
    const maxes = {};
    cardData.regions.forEach(r => {
      maxes[r.name] = Math.max(...r.districts.map(d => d.total), 1);
    });
    return maxes;
  }, [cardData.regions]);

  if (!cardData.regions.length) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-zinc-200 p-12 text-center">
        <div className="text-zinc-400 mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-zinc-700 mb-2">No enrollment data</h3>
        <p className="text-sm text-zinc-500">No districts with enrollment found for this filter.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-zinc-200 p-3">
        <div className="flex items-center justify-between">
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">Show:</span>
            <div className="flex rounded-md border border-zinc-200 overflow-hidden">
              <button
                onClick={() => setViewMode('raw')}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  viewMode === 'raw'
                    ? 'bg-zinc-800 text-white'
                    : 'bg-white text-zinc-600 hover:bg-zinc-50'
                }`}
              >
                Raw
              </button>
              <button
                onClick={() => setViewMode('percent')}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  viewMode === 'percent'
                    ? 'bg-zinc-800 text-white'
                    : 'bg-white text-zinc-600 hover:bg-zinc-50'
                }`}
              >
                % of Region
              </button>
            </div>
          </div>

          {/* Legend Toggle */}
          <button
            onClick={() => setShowLegend(!showLegend)}
            className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-700"
          >
            <span>{showLegend ? 'Hide' : 'Show'} Legend</span>
            <svg className={`w-4 h-4 transition-transform ${showLegend ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {/* CIP Legend (collapsible) */}
        {showLegend && (
          <div className="mt-3 pt-3 border-t border-zinc-100">
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {cardData.cipLegend.map(cip => (
                <div
                  key={cip.code}
                  className={`flex items-center gap-1.5 text-xs cursor-pointer transition-opacity ${
                    hoveredCip && hoveredCip !== cip.code ? 'opacity-30' : ''
                  }`}
                  onMouseEnter={() => setHoveredCip(cip.code)}
                  onMouseLeave={() => setHoveredCip(null)}
                >
                  <div
                    className="w-3 h-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: cip.color }}
                  />
                  <span className="text-zinc-600 truncate" style={{ maxWidth: 150 }} title={cip.name}>
                    {cip.name.length > 20 ? cip.name.slice(0, 18) + '…' : cip.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Regional Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {cardData.regions.map(region => (
          <div
            key={region.name}
            className="bg-white rounded-lg shadow-sm border border-zinc-200 overflow-hidden"
          >
            {/* Region Header */}
            <div
              className="px-4 py-3 border-b border-zinc-200"
              style={{
                borderLeftWidth: 4,
                borderLeftColor: region.color,
                backgroundColor: `${region.color}08`
              }}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-zinc-800 text-sm">{region.name}</h3>
                <span className="text-xs text-zinc-500">{region.total.toLocaleString()} enrolled</span>
              </div>
            </div>

            {/* Districts */}
            <div className="p-3 space-y-3">
              {region.districts.map(district => {
                const hasEnrollment = district.total > 0;
                const barScale = viewMode === 'percent'
                  ? regionMaxes[region.name]
                  : cardData.maxEnrollment;

                return (
                  <div key={district.name} className={!hasEnrollment ? 'opacity-60' : ''}>
                    {/* District name and total */}
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs truncate ${hasEnrollment ? 'text-zinc-700' : 'text-zinc-400'}`} style={{ maxWidth: 140 }} title={district.name}>
                        {district.name}
                      </span>
                      <span className={`text-xs font-medium ${hasEnrollment ? 'text-zinc-500' : 'text-amber-500'}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {hasEnrollment ? district.total : '0'}
                      </span>
                    </div>

                    {/* Stacked bar or empty indicator */}
                    {hasEnrollment ? (
                      <div className="h-5 bg-zinc-100 rounded-sm overflow-hidden flex">
                        {district.cips.map((cip, idx) => {
                          const width = (cip.enrollment / barScale) * 100;
                          const isHovered = hoveredCip === cip.code;
                          const isMuted = hoveredCip && !isHovered;

                          return (
                            <div
                              key={cip.code}
                              className="h-full transition-opacity relative group"
                              style={{
                                width: `${width}%`,
                                backgroundColor: cip.color,
                                opacity: isMuted ? 0.2 : (isHovered ? 1 : 0.85)
                              }}
                              title={`${cip.name}: ${cip.enrollment}`}
                              onMouseEnter={() => setHoveredCip(cip.code)}
                              onMouseLeave={() => setHoveredCip(null)}
                            >
                              {/* Show enrollment number if segment is wide enough */}
                              {width > 8 && (
                                <span className="absolute inset-0 flex items-center justify-center text-[9px] text-white font-medium">
                                  {cip.enrollment}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="h-5 bg-amber-50 border border-dashed border-amber-200 rounded-sm flex items-center justify-center">
                        <span className="text-[10px] text-amber-500">No enrollment — opportunity</span>
                      </div>
                    )}
                  </div>
                );
              })}

              {region.districts.length === 0 && (
                <div className="text-xs text-zinc-400 text-center py-4">
                  No districts in this region
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
